<template>
    <ReoDialog
        v-if="show"
        @cancel="$emit('change',false)"
        :showButton="false"
    >
        <div class="container">
            <ul>
                <li>
                    <label>{{ $t('payment_history.id_label') }}</label>
                    <span>#{{ payment.id }}</span>
                </li>
                <li>
                    <label>{{ $t('payment_history.date_label') }}</label>
                    <span>{{ $t( 'date_format.default' ,{

                        year: timeResolve( payment.createdAt ).year,
                        month: timeResolve( payment.createdAt ).month,
                        day: timeResolve( payment.createdAt ).dateOfMonth

                    }) }}</span>
                </li>
                <li>
                    <label>{{$t('payment_history.tax_label')}}</label>
                    <span>{{ `${payment.currency} ${payment.totalTax.toFixed(2)}`}}</span>
                </li>
                <li>
                    <label>{{ $t('payment_history.total_label') }}</label>
                    <span class="total">
                        <span :class="{ 'del sub-note': Number(payment.refundingAmount) === 0 && Number(payment.refundedAmount) > 0 }">
                            {{ `${payment.currency} ${payment.amount}` }}
                        </span>
                        <template v-if="Number(payment.refundingAmount) > 0">
                            <span class="red">
                                {{ $t('payment_history.refunding') }}
                            </span>
                        </template>
                        <template v-else-if="Number(payment.refundedAmount) > 0">
                            <span class="red">
                                {{ `${payment.currency} ${(parseFloat(payment.amount) - parseFloat(payment.refundedAmount)).toFixed(2)}` }} {{ $t('payment_history.refunded') }}
                            </span>
                        </template>
                    </span>
                </li>
                <li>
                    <label>{{ $t('payment_history.status_label') }}</label>
                    <span>
                        <div
                            v-if="payment.status.currStage === 'payment'
                                && payment.status.paymentStatus === paymentStatus.CREATED "
                        >
                            {{ $t(`payment_status.created`) }}
                        </div>

                        <!-- paypal收款状态 -->
                        <span
                            v-else-if="payment.status.currStage === currentStage.PAYMENT
                                && payment.status.paymentStatus===paymentStatus.CLOSED && payment.closedReason==='paymentDenied'">
                            {{ $t(`payment_status.closed`) }}
                        </span>

                        <!-- 订单未支付过期 -->
                        <span v-else-if="payment.status.currStage === currentStage.PAYMENT
                            && payment.status.paymentStatus===paymentStatus.CLOSED && payment.closedReason==='expired'">
                            {{ $t(`payment_status.expired`) }}
                        </span>

                        <!-- 正在退款 -->
                        <span v-else-if="payment.status.currStage === currentStage.POSTSALE
                            && payment.status.postSaleStatus===postSaleStatus.REFUNDING
                            && Number(payment.refundingAmount)!==0">
                            {{ $t(`payment_status.refunding`) }}
                        </span>

                        <!-- 全部退款 -->
                        <span v-else-if="payment.status.currStage === currentStage.POSTSALE
                            && (payment.status.postSaleStatus===postSaleStatus.CLOSED || payment.status.postSaleStatus===postSaleStatus.REFUNDING)
                            && Number(payment.refundingAmount)===0">
                            {{ $t(`payment_status.refunded`) }}
                        </span>

                        <span v-else>
                            {{ $t(`payment_status.${payment.status.paymentStatus}`) }}
                        </span>
                    </span>
                </li>
            </ul>
            <span
                v-if=" (payment.status.currStage === 'payment'&&payment.status.paymentStatus === paymentStatus.CREATED )
                    || payment.status.paymentStatus === paymentStatus.COMPLETED"
                class="buttons"
            >
                <a class="ui-button empty-button"
                    v-if="payment.status.currStage === 'payment'
                        && payment.status.paymentStatus === paymentStatus.CREATED "
                    @click='$emit("goPay", payment.id)'>
                    {{ $t('payment_history.pay_now') }}
                </a>
                <!-- 完成 -->
                <router-link class="ui-button empty-button"
                    v-if="payment.status.paymentStatus === paymentStatus.COMPLETED"
                    :to="`/user/invoice/${payment.id}/`"
                    target="_blank">
                    {{ $t('payment_history.invoice') }}
                </router-link>
            </span>
            <ul v-for="(item ) in payment.items" :key="item.id">
                <li>
                    <label>{{ $t('payment_history.plan_label') }}</label>
                    <span>
                        {{ item.name }}
                        <span v-if="item.productType!=='cloud_data_add_on_plan' && item.plan.cycle">

                            ({{ $tc( `cycle_short.${item.plan.cycle.interval.unit}`, item.plan.cycle.interval.frequency, {

                                count: item.plan.cycle.interval.frequency

                            }) }})
                        </span>
                        *
                        {{ item.quantity }}

                    </span >
                </li>
                <li v-if="item.context.associateSimCards && isLocked(item)">
                    <label>{{ $t('payment_history.device_label') }}</label>
                    <span>
                        <span v-for="simCard in item.context.associateDevices" :key="simCard.uid">
                            ({{$t('dashboard.device_detail.main_card.uid')+simCard.uid}})
                        </span>
                    </span>
                </li>
                <li v-else-if="item.context.associateSimCards && item.context.associateSimCards.length>0">
                    <label>{{ $t('payment_history.sim_card_label') }}</label>
                    <span>
                        <span v-for="simCard in item.context.associateSimCards" :key="simCard.iccid">
                            {{simCard.simCardCode}}({{$t('dashboard.device_detail.main_card.iccid')+simCard.iccid}})
                        </span>
                    </span>
                </li>
            </ul>
        </div>
    </ReoDialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import ReoDialog from '@/basic/Dialog.vue';
import { IOrder, paymentStatus, currStage as currentStage, postSaleStatus, IOrderItem, IPlan } from '@reolink-cloud/interface';
import { timeResolve } from '@/common/js/date';
type TExtendOrderItem = IOrderItem & { plan: IPlan; };
type TExtendOrder = IOrder & {
    totalTax: number;
    items: TExtendOrderItem[];
};

export default Vue.extend({
    components:{
        ReoDialog
    },
    model: {
        prop: 'show',
        event: 'change'
    },
    props:{
        show: Boolean,
        payment: Object as PropType<TExtendOrder>
    },
    computed:{
        isLocked(): ( item: TExtendOrderItem ) => boolean {

            return ( item: TExtendOrderItem ) =>
                !!item.context?.associateDevices?.length &&
                !!item.context?.associateSimCards?.length;

        },
    },
    data() {

        return {
            paymentStatus,
            currentStage,
            postSaleStatus,
            timeResolve
        };

    },
});
</script>

<style lang="less" scoped>
/deep/ .reo-dialog-inner{
    max-width: 1165px !important;
    background-color: @bg_01 !important;
}
.container{
    display: flex;
    flex-direction: column;
    gap: 24px;
    position: relative;
    .buttons{
        position: absolute;
        right: 0;
        top: 0;
    }
}
ul{
    display: flex;
    flex-direction: column;
    gap: 22px;
    &:not(:first-of-type){
        width: 1000px;
        padding: 24px;
        background-color: @bg_02;
        box-sizing: border-box;
    }
    li{
        display: flex;
        gap: 40px;
        label{
            color: @font_secondary;
            font-size: 18px;
            font-weight: 500;
            width: 200px;
        }
        span{
            color: @font_primary;
            font-size: 18px;
            font-weight: 500;
        }
    }
}
</style>
